/* The Funds page as a PORTFOLIO TABLE — one line per fund (Tier-2 D3,
 * Ale ruling 2026-09-05: "Funds list as a portfolio table — ledger
 * columns, doors").
 *
 * The rows, the sort, the find box and the day-0 rule live HERE; the page
 * only draws them. Every figure on a row is the fund LEDGER's
 * (ledgerWords.ts, GET /api/funds/ledger — one sweep): the table adds,
 * sorts and filters what the bulk read served and re-derives NOTHING.
 * The identity half of a row (name, form, jurisdiction, contact) is the
 * fund list's; the figures are the ledger's.
 *
 * Pure (no runtime imports) so the bare node runner can prove it — see
 * tests/unit/fundsTable.test.mjs. */

import type { FundLedger } from './ledgerWords'

/** What GET /api/funds says about a fund that the ledger does not. */
export interface FundIdentity {
  id: string
  name: string
  legal_form?: string | null
  jurisdiction?: string | null
  client_contact_name?: string | null
  client_contact_email?: string | null
}

/** One line of the table. `ledger` is null while the bulk read has not
 *  answered (or failed) — the figure cells then read "—", never 0. */
export interface FundRow {
  id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  /** The contact as printed: the name, else the address, else null. */
  contact: string | null
  ledger: FundLedger | null
  /** Nothing on the record yet — see isDay0. */
  day0: boolean
}

export type SortKey =
  'fund' | 'to_book' | 'to_decide' | 'breaks' | 'owed' | 'signoff' | 'changed' | 'contact'

export type SortDir = 'asc' | 'desc'

export interface Sort {
  key: SortKey
  dir: SortDir
}

/** Header groups carry the flat-table tint (flat-v2, Ale 2026-09-05):
 *  identity | open work | the record's own facts. */
export type ColumnGroup = 'id' | 'work' | 'rec'

export interface Column {
  key: SortKey
  /** The header, uppercase in the mono label face. */
  label: string
  group: ColumnGroup
  /** Right-aligned tabular figure column. */
  num: boolean
  /** The queue this column's figure opens, as a workbench slug — the door
   *  carries ?fund=<id>. null for the columns that are not a queue. */
  queue: string | null
}

/** THE columns, in the ruled order. FUND · TO BOOK · TO DECIDE · BREAKS ·
 *  OWED · AWAITING SIGN-OFF · CHANGED SINCE · CONTACT. */
export const COLUMNS: readonly Column[] = [
  { key: 'fund', label: 'Fund', group: 'id', num: false, queue: null },
  { key: 'to_book', label: 'To book', group: 'work', num: true, queue: 'record-keeping' },
  { key: 'to_decide', label: 'To decide', group: 'work', num: true, queue: 'record-keeping' },
  { key: 'breaks', label: 'Breaks', group: 'work', num: true, queue: 'reconciliation' },
  { key: 'owed', label: 'Owed', group: 'work', num: true, queue: 'chase-request' },
  {
    key: 'signoff',
    label: 'Awaiting sign-off',
    group: 'work',
    num: true,
    queue: 'ownership-verification',
  },
  { key: 'changed', label: 'Changed since', group: 'rec', num: true, queue: null },
  { key: 'contact', label: 'Contact', group: 'rec', num: false, queue: null },
]

/** Nothing on this fund's record yet: no obligation was ever demanded and
 *  no open work stands anywhere except documents waiting to be booked.
 *  A day-0 fund reads as a sentence, not as a row of zeros. */
export function isDay0(l: FundLedger | null): boolean {
  if (!l) return false
  return (
    l.owed.demanded === 0 &&
    l.owed.total === 0 &&
    l.breaks.open === 0 &&
    l.awaiting_signoff === 0 &&
    l.to_decide === 0 &&
    l.to_walk === 0
  )
}

/** "nothing on the record yet · 12 to book" — the day-0 line. The figure
 *  is a door like any other; at zero the clause is dropped. */
export function day0Words(l: FundLedger): string {
  return l.to_book > 0
    ? `nothing on the record yet · ${l.to_book} to book`
    : 'nothing on the record yet'
}

/** The row's contact as printed: the stored name, else the address. */
export function contactOf(f: FundIdentity): string | null {
  const name = (f.client_contact_name ?? '').trim()
  if (name) return name
  const mail = (f.client_contact_email ?? '').trim()
  return mail || null
}

/** The fund's second line: "SCSp · Luxembourg". Neither is invented — a
 *  fund with no form on the record simply prints the other half. */
export function metaWords(row: { legal_form: string | null; jurisdiction: string | null }): string {
  return [row.legal_form, row.jurisdiction]
    .filter((s): s is string => !!s && !!s.trim())
    .join(' · ')
}

/** The rows: the fund list joined to the bulk ledger by fund id. */
export function buildRows(funds: FundIdentity[], ledgers: Record<string, FundLedger>): FundRow[] {
  return funds.map((f) => {
    const ledger = ledgers[f.id] ?? null
    return {
      id: f.id,
      name: f.name,
      legal_form: f.legal_form ?? null,
      jurisdiction: f.jurisdiction ?? null,
      contact: contactOf(f),
      ledger,
      day0: isDay0(ledger),
    }
  })
}

/** The figure a column prints — the LEDGER's, never re-derived. null when
 *  the ledger has not answered for this fund. */
export function figureOf(row: FundRow, key: SortKey): number | null {
  const l = row.ledger
  if (!l) return null
  switch (key) {
    case 'to_book':
      return l.to_book
    case 'to_decide':
      return l.to_decide
    case 'breaks':
      return l.breaks.open
    case 'owed':
      return l.owed.total
    case 'signoff':
      return l.awaiting_signoff
    case 'changed':
      return l.changed_since.count
    default:
      return null
  }
}

/** "5 · 3 optional": the muted split the OWED cell carries, or null when
 *  nothing of the owed total is optional. */
export function optionalWords(row: FundRow): string | null {
  const n = row.ledger?.owed.optional ?? 0
  return n > 0 ? `${n} optional` : null
}

/** Does the find box keep this row? Name, form, jurisdiction and contact —
 *  the four things printed in the FUND and CONTACT cells. */
export function matchesFind(row: FundRow, find: string): boolean {
  const q = find.trim().toLowerCase()
  if (!q) return true
  return [row.name, row.legal_form, row.jurisdiction, row.contact]
    .filter((s): s is string => !!s)
    .some((s) => s.toLowerCase().includes(q))
}

export function filterRows(rows: FundRow[], find: string): FundRow[] {
  return rows.filter((r) => matchesFind(r, find))
}

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true })

/** Sorted by any column; ties always fall back to the name A–Z, so the
 *  order is total and a re-sort never shuffles equal rows. A row whose
 *  ledger has not answered sorts last on a figure column, both ways. */
export function sortRows(rows: FundRow[], sort: Sort): FundRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  const byName = (a: FundRow, b: FundRow) => collator.compare(a.name, b.name)
  const out = rows.slice()
  out.sort((a, b) => {
    if (sort.key === 'fund') return sign * byName(a, b)
    if (sort.key === 'contact') {
      // No contact stored sorts last whichever way the column is turned —
      // it is a gap to close, not a name.
      if (!a.contact !== !b.contact) return a.contact ? -1 : 1
      const c = collator.compare(a.contact ?? '', b.contact ?? '')
      return c ? sign * c : byName(a, b)
    }
    const fa = figureOf(a, sort.key)
    const fb = figureOf(b, sort.key)
    if (fa === null || fb === null) {
      if (fa === fb) return byName(a, b)
      return fa === null ? 1 : -1
    }
    return fa === fb ? byName(a, b) : sign * (fa - fb)
  })
  return out
}

/** Clicking a header: a new column takes its natural first direction —
 *  A–Z for the two word columns, biggest-first for every figure — and
 *  clicking the column already sorted turns it round. */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  return { key, dir: key === 'fund' || key === 'contact' ? 'asc' : 'desc' }
}

/** The default: name A–Z. */
export const DEFAULT_SORT: Sort = { key: 'fund', dir: 'asc' }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A date in a TABLE CELL keeps a two-digit day, so the months line up
 *  down the column (Ale 2026-09-05, flat v3): "02 Sep 2026". Anything
 *  unparseable passes through untouched. */
export function tableDate(v: string | null | undefined): string | null {
  if (!v) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!m) return v
  return `${m[3]} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
}

/** The CHANGED SINCE header carries the one date the whole list is read
 *  against — the ledger sweep's `since`. */
export function changedSinceLabel(since: string | null): string {
  const d = tableDate(since)
  return d ? `Changed since ${d}` : 'Changed since'
}

/** The footer: what the officer just looked at, in the ledger's own
 *  figures. Sums only — nothing derived that the ledger did not state. */
export interface TableFoot {
  funds: number
  withBreaks: number
  owed: number
  toBook: number
}

export function footOf(rows: FundRow[]): TableFoot {
  const foot: TableFoot = { funds: rows.length, withBreaks: 0, owed: 0, toBook: 0 }
  for (const r of rows) {
    if (!r.ledger) continue
    if (r.ledger.breaks.open > 0) foot.withBreaks += 1
    foot.owed += r.ledger.owed.total
    foot.toBook += r.ledger.to_book
  }
  return foot
}

/** "24 funds · 5 with breaks · 17 owed · 114 to book". */
export function footWords(f: TableFoot): string {
  return [
    `${f.funds} ${f.funds === 1 ? 'fund' : 'funds'}`,
    `${f.withBreaks} with breaks`,
    `${f.owed} owed`,
    `${f.toBook} to book`,
  ].join(' · ')
}
